import { useEffect, useState } from "react";
import { Mascot } from "../components/Mascot";
import { CARD_SAYS, TITLE } from "../lib/card";
import { countdown, OPENS, stageAt, type Stage } from "../lib/landing";
import type { MascotPose } from "../lib/mascot";
import "./Landing.css";

/**
 * The bare address (his ask, 16 Oct): the title slide's look as a page, for anyone who opens the
 * address before the day, with a countdown to the hall opening. It changes by itself on the day:
 * from 1pm "We're on" points anyone who typed the address at the QR code on the big screen, and
 * after 6pm it is a wrap. The demo links that used to be here are gone from it (his call); the
 * pretend screens still open at their own addresses (?mock=1). ?at=2026-10-18T14:00 shows the page
 * as it is at that time, for pictures.
 */

const SAYS: Record<Stage, string> = {
  before: CARD_SAYS,
  on: "We have started. Scan the code on the big screen and get on court.",
  after: "It is over. Most of you were adequate.",
};

const POSE: Record<Stage, MascotPose> = { before: "crown", on: "calling", after: "smug" };

/** The clock the page reads: the device's, or a fixed time from ?at= for pictures. */
function useNow(): number {
  const at = Date.parse(new URLSearchParams(location.search).get("at") ?? "");
  const [now, setNow] = useState(() => (Number.isNaN(at) ? Date.now() : at));
  useEffect(() => {
    if (!Number.isNaN(at)) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [at]);
  return now;
}

const UNITS = ["days", "hours", "mins", "secs"] as const;

export function Landing() {
  const now = useNow();
  const stage = stageAt(now);
  useEffect(() => {
    document.title = "BDayminton 2026";
  }, []);
  const left = countdown(now, OPENS);
  return (
    <main className="lp" data-stage={stage}>
      <div className="lp-in">
        <div className="lp-title">
          <h1 className="lp-name lp-display">{TITLE.name}</h1>
          <p className="lp-sub">{TITLE.sub}</p>
          <p className="lp-when">{TITLE.when}</p>
          <p className="lp-year lp-display">{TITLE.year}</p>
        </div>
        <div className="lp-now">
          {stage === "before" ? (
            <>
              <ol className="lp-count" aria-label={`${left[0]} days, ${left[1]} hours, ${left[2]} minutes until 1pm Sunday`}>
                {left.map((n, i) => (
                  <li key={UNITS[i]} className="lp-unit">
                    <span className="lp-num lp-display">{i === 0 ? n : String(n).padStart(2, "0")}</span>
                    <span className="lp-label">{n === 1 && i === 0 ? "day" : UNITS[i]}</span>
                  </li>
                ))}
              </ol>
              <p className="lp-until">until the hall opens, 1pm Sunday</p>
            </>
          ) : stage === "on" ? (
            <div className="lp-slab">
              <p className="lp-big lp-display">We're on</p>
              <p className="lp-line">Scan the QR code on the big screen to join.</p>
            </div>
          ) : (
            <div className="lp-slab">
              <p className="lp-big lp-display">That's a wrap</p>
              <p className="lp-line">Thank you for coming.</p>
            </div>
          )}
        </div>
        <div className="lp-host">
          <p className="lp-says">{SAYS[stage]}</p>
          <Mascot pose={POSE[stage]} className="lp-mascot" />
        </div>
      </div>
    </main>
  );
}
